import React, { useState, useCallback, useEffect, useRef } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  MenuItem,
  Tooltip,
  Card,
} from "@mui/material";
import {
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  StyledButton,
  OutlinedIconFab,
  theme,
  StyledMultilineTextField,
} from "../StyledComponents";
import Suggestions from "../Utils/Suggestions";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import Icon from "../Utils/Icons";
import { DiagnosisTable } from "../Sections/DiagnosisTable";
import { durationType } from "../initialData/initialData";

const DiagnosisSimplified = ({
  sectionName,
  activeSection,
  suggestions,
  aiSuggestionToggle,
  ruleSuggestionToggle,
  handleCrossButtonClick,
  data,
  setData,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  console.log("Inside Diagnosis", data);

  const [listeningOn, setListeningOn] = useState(false);
  const [newDiagnosis, setNewDiagnosis] = useState({
    term: "",
    clinical_note: "",
    duration: "",
    durationType: "days",
  });

  const [diagnosisName, setDiagnosisName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [diagnosis, setDiagnosis] = useState(data);
  const [validationErrors, setValidationErrors] = useState({
    term: "",
    duration: "",
  });

  useEffect(() => {
    setDiagnosis(data);
  }, [data]);

  useEffect(() => {
    if (!newDiagnosis.durationType) {
      setNewDiagnosis((prev) => ({
        ...prev,
        durationType: "days",
      }));
    }
  }, [newDiagnosis.durationType]);

  const validateFields = () => {
    const errors = {};

    if (!newDiagnosis.term && diagnosisName.length === 0) {
      errors.term = "Diagnosis Name is a mandatory field.";
    }

    setValidationErrors((prev) => ({
      ...prev,
      term: errors.term || "",
    }));

    return Object.keys(errors).length === 0; // Return true if no errors
  };
  // Function to handle adding a new row
  const handleAdd = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }
    if (validationErrors.duration) {
      return;
    }

    let newDetails = {
      ...newDiagnosis,
      id: null,
      durationType: newDiagnosis.duration ? newDiagnosis.durationType : "",
    };

    // Add the new term to the complaints list
    let updatedDetails = [...diagnosis, newDetails];
    console.log("Diagnosis Updated Details", updatedDetails);
    setDiagnosis(updatedDetails);
    setNewDiagnosis({
      term: "",
      clinical_note: "",
      duration: "",
      durationType: "days",
    });
    setValidationErrors({}); // Clear errors after successful submission
    setDiagnosisName("");
    setData(updatedDetails);
  };

  // Function to handle input changes
  const handleInputChange = (field, value) => {
    let newDetails = {
      ...newDiagnosis,
      [field]: value,
    };

    if (field === "duration") {
      const floatValue = parseFloat(value);
      const intValue = parseInt(value, 10);

      if ((floatValue && isNaN(floatValue)) || floatValue <= 0) {
        setValidationErrors((prev) => ({
          ...prev,
          duration: "Duration must be a positive integer",
        }));
      } else if (floatValue && floatValue !== intValue) {
        setValidationErrors((prev) => ({
          ...prev,
          duration: "Duration must be a positive integer",
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          duration: "",
        }));
      }
    }

    setNewDiagnosis(newDetails);
  };

  // Function to handle row edit
  const handleEditRow = (index) => {
    setDiagnosisName(diagnosis[index].term);
    setNewDiagnosis({ ...diagnosis[index] });
    setEditingIndex(index);
    // handleDeleteRow(index); // Remove the row from the list temporarily
  };
  console.log("Editing Index", editingIndex);
  const handleUpdate = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }
    if (validationErrors.duration) {
      return;
    }

    let newDetails;

    newDetails = {
      ...newDiagnosis,
      source: undefined,
      durationType: newDiagnosis.duration ? newDiagnosis.durationType : "",
    };

    let updatedDetails = diagnosis.map((data, i) =>
      i == editingIndex ? newDetails : data
    );
    setDiagnosis(updatedDetails);
    setEditingIndex(null);
    setNewDiagnosis({
      term: "",
      clinical_note: "",
      duration: "",
      durationType: "days",
    });
    setDiagnosisName("");
    setData(updatedDetails);
  };
  // Function to handle row delete
  const handleDeleteRow = (index) => {
    let updatedDetails = diagnosis.filter((_, i) => i !== index);
    setDiagnosis(updatedDetails);
    setData(updatedDetails);
  };
  const memoizedHandleCrossButtonClick = useCallback(
    (sectionName, suggestionName, type) => {
      handleCrossButtonClick(sectionName, suggestionName, type);
    },
    [handleCrossButtonClick]
  );
  const handleClearDiagnosisName = () => {
    setValidationErrors({});
    setDiagnosisName("");
    setNewDiagnosis({
      ...newDiagnosis,
      term: "", // Use computed property name to set the field dynamically
      duration: "",
      durationType: "days",
    });
  };

  const handleConditionTyped = (value) => {
    setDiagnosisName(value);
    handleInputChange("term", value);
  };

  console.log("Diagnosis data", diagnosis);
  return (
    <ThemeProvider theme={theme}>
      {activeSection && (
        <Grid item xs={12}>
          <Suggestions
            sectionName={sectionName}
            suggestions={suggestions}
            activeSection={activeSection}
            aiSuggestionToggle={aiSuggestionToggle}
            ruleSuggestionToggle={ruleSuggestionToggle}
            handleCrossButtonClick={memoizedHandleCrossButtonClick}
            handleSuggestionClick={handleConditionTyped}
          />
        </Grid>
      )}
      {activeSection && (
        <Card
          sx={{
            borderRadius: "10px",
            boxShadow: "0 4px 4px 0 #C7C7CC inset",
            backgroundColor: "#FFF",
          }}
        >
          <Grid item xs={12} sm={12} md={12}>
            <Grid
              container
              spacing={2}
              sx={{
                marginTop: "5px",
                padding: "10px",
              }}
            >
              <Grid item xs={12} sm={12} md={4}>
                <StyledTextField
                  value={diagnosisName}
                  onChange={(e) => {
                    handleConditionTyped(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div style={{ marginRight: "5px" }}>
                          <Tooltip title="Clear">
                            <OutlinedIconFab
                              size="small"
                              variant="contained"
                              onClick={handleClearDiagnosisName}
                            >
                              <Icon name="clearTextField" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>

                        {/* <>
                          {isAiEnabled &&
                            (listeningOn === false ? (
                              <Tooltip title="Start Listening">
                                <OutlinedIconFab
                                  onClick={() => {
                                    startRecording();
                                    setListeningOn(true);
                                  }}
                                  variant="contained"
                                  color="defaultColor"
                                  size="small"
                                >
                                  <Icon name="micNotInUse" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Stop Listening">
                                <OutlinedIconFab
                                  onClick={() => {
                                    stopRecording("Diagnosis");
                                    setListeningOn(false);
                                  }}
                                  variant="outlined"
                                  color="defaultColor"
                                  size="small"
                                >
                                  <Icon name="micInUse" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            ))}
                        </> */}
                      </div>
                    ),
                  }}
                  label="Diagnosis Name"
                  fullWidth
                  error={!!validationErrors.term} // Highlight field in red if there's an error
                  helperText={validationErrors.term} // Show error message
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <StyledTextField
                  type="number"
                  value={newDiagnosis.duration}
                  error={!!validationErrors.duration}
                  helperText={validationErrors.duration}
                  onChange={(e) =>
                    handleInputChange("duration", e.target.value)
                  }
                  label="Duration"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <StyledTextField
                  select
                  value={newDiagnosis.durationType || "days"}
                  onChange={(e) =>
                    handleInputChange("durationType", e.target.value)
                  }
                  placeholder="Select Duration Type"
                  fullWidth
                  label="Duration Type"
                >
                  {durationType.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                      sx={{ fontSize: "12px" }}
                    >
                      {type.display}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <StyledMultilineTextField
                  value={newDiagnosis.clinical_note}
                  onChange={(e) =>
                    handleInputChange("clinical_note", e.target.value)
                  }
                  placeholder="Clinical Notes"
                  fullWidth
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={1}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <StyledButton
                  bgColor="#0E3F66"
                  onClick={editingIndex != null ? handleUpdate : handleAdd}
                  variant="contained"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                    width: "100%",
                  }}
                >
                  {editingIndex != null ? "Update" : "Add"}
                </StyledButton>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      )}
      {diagnosis && (
        <DiagnosisTable
          data={diagnosis}
          activeSection={activeSection}
          handleEditRow={handleEditRow}
          handleDeleteRow={handleDeleteRow}
        />
      )}
    </ThemeProvider>
  );
};

export default DiagnosisSimplified;
